import api from "./Api";
import format from "date-fns/esm/fp/format/index.js";
import fileDownload from 'js-file-download';
import { PageResponse } from '@models/PageResponse';
import { MDFe } from "model/mdfe/MDFe";
import { MDFeFilter } from "model/mdfe/MDFeFilter";

/* campos do evento de encerramento (dtEnc, cUF e cMun), com os nomes do MDFeCloseDTO do backend */
export interface MDFeClosure {
    closureDate: string,
    stateCode: string,
    cityCode: string
}

/* município da UF do encerramento (MDFeCityDTO): o code é o código IBGE de 7 dígitos */
export interface MDFeCity {
    code: string,
    name: string
}

const toRequestFilters = (filters: MDFeFilter) => ({
    status: filters.status,
    initialDate: filters.initialDateObj ? format("dd/MM/yyyy", filters.initialDateObj) : null,
    finalDate: filters.finalDateObj ? format("dd/MM/yyyy", filters.finalDateObj) : null,
    minValue: filters.vlrMinCarga,
    maxValue: filters.vlrMaxCarga,
    startUf: filters.startUf,
    endUf: filters.endUf,
    serie: filters.serie,
    numDoc: filters.numDoc,
    accessKeys: filters.chaveMdfe,
    branchesIds: filters.branchesIds,
    companyId: filters.companyId,
    transactionsIds: filters.ids
});

export default class MDFeListingApi {

    fetchFilteredMDFes = (filters: MDFeFilter, pageIndex: Number, pageSize: Number, orderBy: string, orderDirection: "ASC" | "DESC"): Promise<PageResponse<MDFe>> => {
        const requestData = {
            ...toRequestFilters(filters),
            page: pageIndex,
            size: pageSize,
            sort: `${orderBy},${orderDirection}`
        }

        return api.get('/mdfe', { params: requestData }).then(({ data }) => data);
    };

    exportMDFes = async (filters: MDFeFilter) => {
        try {
            const res = await api.get('/mdfe/export', { responseType: 'blob', params: toRequestFilters(filters) });

            fileDownload(res.data, res.headers.filename)
        } catch (err) {
            console.log("Failed to export MDFes. ", err);
            return null;
        }
    };

    downloadXml = async (mdfe: MDFe) => {
        try {
            const response = await api.get(`/mdfe/${mdfe.id}/download`, {
                responseType: 'blob'
            });

            if (response.status === 200) {
                fileDownload(response.data, response.headers.filename)
            }

            return response;
        } catch (err) {
            console.log("Failed to download the MDFe xml file.", err);
            return null;
        }
    }

    downloadXmlBatch = async (ids: number[]) => {
        try {
            return await api.get(`/mdfe/download/batch`, {
                params: { ids: ids.join(',') },
                responseType: 'blob'
            });
        } catch (err) {
            console.log("Failed to download the MDFe xml batch file.", err);
            return null;
        }
    }

    downloadDamdfe = async (mdfe: MDFe) => {
        try {
            return await api.get(`/mdfe/${mdfe.id}/damdfe`, { responseType: 'blob' });
        } catch (err) {
            console.log("Failed to download DAMDFE. ", err);
            return null;
        }
    }

    downloadDamdfeBatch = async (ids: number[]) => {
        try {
            return await api.get(`/mdfe/damdfe/batch`, {
                params: { ids: ids.join(',') },
                responseType: 'blob'
            });
        } catch (err) {
            console.log("Failed to download the DAMDFE batch file.", err);
            return null;
        }
    }

    downloadDamdfeMerged = async (ids: number[]) => {
        try {
            return await api.get(`/mdfe/damdfe/merged`, {
                params: { ids: ids.join(',') },
                responseType: 'blob'
            });
        } catch (err) {
            console.log("Failed to download the DAMDFE merged file.", err);
            return null;
        }
    }

    cancelMDFe = ({ id }: MDFe, justification: string): Promise<any> => {
        return api.post('/mdfe/cancel', { id, justification });
    }

    closeMDFe = ({ id }: MDFe, closure: MDFeClosure): Promise<any> => {
        return api.post('/mdfe/close', { id, ...closure });
    }

    fetchCities = (stateCode: string): Promise<MDFeCity[]> => {
        return api.get('/mdfe/cities', { params: { stateCode } }).then(({ data }) => data);
    }

    fetchMDFeHistory =(mdfeId: number): Promise<any> => {
        return api.get(`/mdfe/${mdfeId}/history`);
    }

    fetchConfig = async (rootId: number | undefined, branchId: number): Promise<any> => {
        const requestData = {
            rootId,
            branchId
        }
        try {
            return api.get('/mdfe/config', { params: requestData } );
        } catch (err) {
            throw new Error("Ocorreu um erro ao tentar buscar as configurações.");
        }
    }

    saveConfig = async (payload : any): Promise<any> => {
        try {
            return api.post('/mdfe/config', payload);
        } catch (err) {
            throw new Error("Ocorreu um erro ao tentar salvar as configurações.");
        }
    }
}
